// Queue Management - Mixpanel event queue, retry logic, and statistics
// Extracted from Analytics.js for better separation of concerns

import mixpanel from 'mixpanel-browser';
import { debugLog } from '../../utils/debugUtils';
import {
  logCriticalAnalyticsError,
  trackAnalyticsErrorPattern,
} from '../../utils/debugUtils';

// Queue state variables
let mixpanelEventQueue = [];
let mixpanelReady = false;
let retryQueue = [];
let isProcessingRetries = false;

// Debug utilities for monitoring Mixpanel events
export const eventStats = {
  successful: 0,
  failed: 0,
  queued: 0,
  retried: 0,
};

/**
 * Set Mixpanel ready state and process queued events
 */
export const setMixpanelReady = (ready = true) => {
  mixpanelReady = ready;
  if (ready) {
    processMixpanelQueue();
  }
};

/**
 * Get current Mixpanel ready state
 */
export const isMixpanelReady = () => mixpanelReady;

/**
 * Queue Mixpanel event for later processing
 */
export const queueMixpanelEvent = (eventName, eventParams) => {
  if (mixpanelReady) {
    // Mixpanel is ready, try to track immediately
    try {
      mixpanel.track(eventName, eventParams);
    } catch (error) {
      debugLog('Deferred Mixpanel tracking failed:', error);
      // Add to retry queue if immediate tracking fails
      addToRetryQueue(eventName, eventParams);
    }
  } else {
    // Queue for later
    mixpanelEventQueue.push({ eventName, eventParams, timestamp: Date.now() });
    debugLog('Queued Mixpanel event:', eventName);
  }
};

/**
 * Process all queued Mixpanel events
 */
export const processMixpanelQueue = () => {
  if (!mixpanelReady || mixpanelEventQueue.length === 0) return;

  debugLog('Processing queued Mixpanel events:', mixpanelEventQueue.length);

  // Process queued events
  mixpanelEventQueue.forEach(({ eventName, eventParams }) => {
    try {
      mixpanel.track(eventName, eventParams);
    } catch (error) {
      debugLog('Failed to process queued Mixpanel event:', eventName, error);
      // Add failed events to retry queue
      addToRetryQueue(eventName, eventParams);
    }
  });

  // Clear the queue
  mixpanelEventQueue = [];
};

/**
 * Add event to retry queue with exponential backoff
 */
const addToRetryQueue = (eventName, eventParams, attempt = 1) => {
  if (attempt > 3) {
    debugLog('Max retry attempts reached for event:', eventName);
    return;
  }

  retryQueue.push({
    eventName,
    eventParams,
    attempt,
    timestamp: Date.now(),
  });

  // Process retries with exponential backoff
  if (!isProcessingRetries) {
    scheduleRetryProcessing();
  }
};

/**
 * Schedule retry processing with exponential backoff
 */
const scheduleRetryProcessing = () => {
  if (isProcessingRetries) return;

  isProcessingRetries = true;
  setTimeout(() => {
    processRetryQueue();
    isProcessingRetries = false;
  }, 2000); // Wait 2 seconds before retrying
};

/**
 * Process the retry queue
 */
const processRetryQueue = () => {
  if (!mixpanelReady || retryQueue.length === 0) {
    return;
  }

  debugLog('Processing Mixpanel retry queue:', retryQueue.length, 'events');

  const currentQueue = [...retryQueue];
  retryQueue = [];

  currentQueue.forEach(({ eventName, eventParams, attempt }) => {
    try {
      mixpanel.track(eventName, eventParams);
      debugLog('Successfully retried Mixpanel event:', eventName);
      eventStats.retried++;
      eventStats.successful++;
      // Track successful retry
      trackAnalyticsErrorPattern('mixpanel_retry', true);
    } catch (error) {
      debugLog(
        'Retry failed for Mixpanel event:',
        eventName,
        'attempt:',
        attempt,
        error
      );
      eventStats.failed++;
      // Track failed retry
      trackAnalyticsErrorPattern('mixpanel_retry', false);

      // Log critical error if this is the final retry attempt
      if (attempt >= 3) {
        logCriticalAnalyticsError(
          'mixpanel_retry_exhausted',
          `Event failed after ${attempt} retry attempts: ${eventName}`,
          {
            eventName,
            eventParams: JSON.stringify(eventParams),
            error: error.message,
            totalAttempts: attempt,
          },
          'error'
        );
      }

      // Re-queue with incremented attempt count
      addToRetryQueue(eventName, eventParams, attempt + 1);
    }
  });
};

/**
 * Force process all queues (for debug purposes)
 */
export const forceProcessQueues = () => {
  processMixpanelQueue();
  if (!isProcessingRetries) {
    scheduleRetryProcessing();
  }
};

/**
 * Get current queue status (for debug purposes)
 */
export const getQueueStatus = () => ({
  mixpanelReady,
  queuedEvents: mixpanelEventQueue.length,
  retryQueueEvents: retryQueue.length,
  isProcessingRetries,
});

/**
 * Get event statistics (for debug purposes)
 */
export const getEventStats = () => ({ ...eventStats });

/**
 * Clear all analytics queues (for test isolation)
 */
export const clearAnalyticsQueues = () => {
  mixpanelEventQueue = [];
  retryQueue = [];
  isProcessingRetries = false;
  debugLog('🗑️ Analytics queues cleared for test isolation');
};

// Handle page visibility changes to retry failed events when page becomes visible
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden && mixpanelReady && retryQueue.length > 0) {
      debugLog('Page became visible, processing retry queue');
      scheduleRetryProcessing();
    }
  });
}
